/**
 * Nesting metadata sensors under the camera they narrate.
 *
 * A camera running its own analytics (AXIS Object Analytics) delivers those
 * detections on a SEPARATE stream, and ingest provisions that stream as its own
 * `Sensor` document — because it genuinely needs to be one: it carries its own
 * zone, its own pairing, and its own raw records.
 *
 * The consequence is that one physical camera reads as two devices in every
 * flat list, side by side, with nothing to say they belong together. An
 * operator counting sensors counts it twice, and an operator looking for a
 * camera finds an unfamiliar second entry next to it.
 *
 * `Sensor.videoSensor` is the link — set by an operator pairing manually, or by
 * ingest promoting the edge's declaration once the camera exists. These helpers
 * turn that link into the shape a list wants: cameras at the top level, their
 * metadata streams beneath them.
 *
 * Kept as pure functions, deliberately. The table they feed is sorted,
 * searched and paginated, and every one of those steps has to operate on GROUPS
 * rather than rows or a camera gets separated from its own metadata. That logic
 * is much easier to trust when it can be tested without rendering anything.
 */

import type { Ref, Sensor } from "@sitescene/types";

import { SensorDataType } from "@sitescene/types";

/** A parent sensor together with the metadata streams that narrate it. */
export interface SensorGroup {
	/** Metadata streams nested under `parent`. Usually one; never null. */
	children: Sensor[];
	/** A camera, or any sensor that narrates nothing. */
	parent: Sensor;
}

/** One rendered table row, and how deep it sits. */
export interface SensorRow {
	/** 0 for a top-level sensor, 1 for a metadata stream nested under one. */
	depth: 0 | 1;
	sensor: Sensor;
}

/**
 * Flatten groups back into rows, each child directly beneath its parent.
 *
 * Order within a group is fixed here rather than left to the caller: a child
 * separated from its parent is worse than unsorted, so children are never
 * re-sorted independently.
 */
export function flattenSensorGroups(groups: SensorGroup[]): SensorRow[] {
	return groups.flatMap((group) => [
		{ depth: 0 as const, sensor: group.parent },
		...group.children.map((child) => ({ depth: 1 as const, sensor: child })),
	]);
}

/**
 * Group a flat sensor list into cameras and the metadata streams beneath them.
 *
 * A sensor becomes a child only when its `videoSensor` resolves to a sensor
 * PRESENT IN THIS LIST. That condition is the important one: the list is
 * already scoped (to a location, a zone, a search), so a sensor can easily be
 * paired to a camera that is not in it. Such a sensor stays at the top level
 * rather than being hidden — dropping it would make it unreachable from the
 * only screen that lists it.
 *
 * Order is otherwise preserved, so a caller that sorted its input keeps that
 * order among the parents.
 */
export function groupSensorsByCamera(sensors: Sensor[]): SensorGroup[] {
	const byId = new Map(sensors.map((sensor) => [sensor.id, sensor]));

	// Which parent each child belongs to, resolved once. A sensor that points at
	// itself is treated as unpaired: it would otherwise be its own parent and
	// disappear from the list entirely.
	const parentOf = new Map<string, string>();
	for (const sensor of sensors) {
		const parentId = refId(sensor.videoSensor);
		if (!parentId || parentId === sensor.id) continue;
		if (!byId.has(parentId)) continue;
		parentOf.set(sensor.id, parentId);
	}

	const groups: SensorGroup[] = [];
	const groupByParentId = new Map<string, SensorGroup>();

	// Two passes so a child listed BEFORE its parent still lands under it —
	// input order is the caller's sort, not a guarantee about pairing.
	for (const sensor of sensors) {
		if (parentOf.has(sensor.id)) continue;
		const group: SensorGroup = { children: [], parent: sensor };
		groups.push(group);
		groupByParentId.set(sensor.id, group);
	}

	for (const sensor of sensors) {
		const parentId = parentOf.get(sensor.id);
		if (!parentId) continue;
		// The parent is in `byId`, but it may itself be someone's child (a chain
		// the data model does not intend). Attach to it anyway if it heads a
		// group; otherwise leave the sensor top-level rather than lose it.
		const group = groupByParentId.get(parentId);
		if (group) {
			group.children.push(sensor);
		} else {
			groups.push({ children: [], parent: sensor });
		}
	}

	return groups;
}

/**
 * Whether this sensor is a metadata stream rather than a device in its own
 * right — analytics ABOUT another sensor, not an independent observation.
 *
 * Keyed on the sensor's TYPE, and deliberately not on whether it has been
 * paired to a camera. The two criteria answer different questions, and which
 * one a surface wants follows from what that surface is FOR:
 *
 *  - The sensor LIST and the health sweep key on `videoSensor`, because both
 *    need to know the camera. Nesting has nowhere to put a sensor whose parent
 *    is unknown, and the health exemption is only justified when something else
 *    is carrying that sensor's liveness. An unpaired metadata stream therefore
 *    stays listed and stays alarmed.
 *
 *  - The MAP keys on type, because a pin asserts a physical place and a
 *    metadata stream never has one of its own — paired or not, it is the camera
 *    it narrates. An unpaired one is not a device we have yet to locate; it is
 *    a stream whose camera we have yet to resolve.
 *
 * Only `object_analytics` qualifies. Air quality, motion, contact and the rest
 * are real devices at real coordinates and belong on a map.
 */
export function isMetadataStream(sensor: Sensor): boolean {
	return sensor.type === SensorDataType.ObjectAnalytics;
}

/**
 * Every sensor in a group — the parent and its children.
 *
 * Used by search: a group is kept when ANY of its members match, so typing a
 * metadata stream's name still shows the camera it belongs to instead of an
 * orphaned child row.
 */
export function sensorsInGroup(group: SensorGroup): Sensor[] {
	return [group.parent, ...group.children];
}

/** Resolve a `Ref<Sensor>` to its id, whether it is a string or populated. */
function refId(ref: Ref<Sensor> | undefined): string | undefined {
	if (!ref) return undefined;
	return typeof ref === "string" ? ref : ref.id;
}
